import axios from 'axios';
import {ENDPOINT_URL, GET_FLOOR_PLAN} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a floor plan row identifier.
 *
 * `floor_id` is what Canvas sends and what every row action reads - see
 * `handleDeleteClick` in `ConfigureFloorPlan.jsx`. Read from the component
 * rather than guessed: the Captive Portals resolver looked for `id` when
 * Canvas was sending `captive_id`, so every row normalised to null and the
 * lookup reported an empty estate against 71 real records.
 *
 * @param {object} item Canvas floor plan row
 * @return {*} identifier
 */
function floorPlanId(item) {
	return item.floor_id ?? item.id;
}

/**
 * Normalize a floor plan row into an agent candidate.
 * @param {object} item Canvas floor plan row
 * @return {object|null} agent candidate
 */
function normalizeFloorPlan(item) {
	const id = floorPlanId(item);
	const name = item.floor_name ?? item.name;
	if (id == null || !name) return null;
	const values = {plan_name: String(name)};
	if (item.floor_type) values.plan_type = String(item.floor_type);
	if (item.total_aps != null) {
		values.plan_access_points = String(item.total_aps);
	}
	if (item.total_switches != null) {
		values.plan_switches = String(item.total_switches);
	}
	return {
		entity_type: 'floor_plan',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: optionalText(item.site_id),
		site_name: null,
		serial_no: null,
		field_values: values,
	};
}

// Words that mean no plan was actually named - "configure the floor plan"
// with nothing left over. Unfiltered, a bare "plan" matches every row.
const GENERIC_QUERIES = new Set([
	'floor plan', 'floor plans', 'floorplan', 'floorplans',
	'plan', 'plans', 'floor', 'floors', 'a plan', 'the plan',
]);

/**
 * Resolve floor plan candidates within one Site.
 *
 * The Site is in the path, not the query string - `floorplan/<siteId>/` -
 * which is why this does not reuse the shared search parameter shape. Canvas
 * returns the whole list for a site, so the narrowing is done here.
 *
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveFloorPlans(params) {
	const query = String(params.query || '').trim();
	const siteId = String(params.site_id || '').trim();
	const browse = params.browse === true;
	if (!siteId) throw new Error('site_required');
	if (!query && !browse) throw new Error('lookup_query_required');
	if (GENERIC_QUERIES.has(query.toLowerCase())) {
		throw new Error('no_target_specified');
	}
	const loadRows = async () => {
		const url = `${ENDPOINT_URL}${GET_FLOOR_PLAN}${encodeURIComponent(siteId)}/`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	const rows = await loadRows();
	replaceResolvedEntities('floor_plan', rows, floorPlanId);
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		return {
			candidates: rows.map(normalizeFloorPlan).filter(Boolean).slice(0, limit),
		};
	}
	const {candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows: async () => rows,
		normalizeRow: normalizeFloorPlan,
		fuzzy: params.fuzzy === true,
	});
	return {candidates};
}

/**
 * Return a retained floor plan row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedFloorPlan(entityId) {
	return getResolvedEntity('floor_plan', entityId);
}
